import React, { memo as Memo, useMemo, useRef, useState, useEffect } from 'react'

//* HOC's
import { withDataContext, withUIContext, withAccountContext } from 'context'
import { useRouter } from 'next/router'

//* Components
import { Page, Text, Container, CustomLink, Icon, AnimationWrapp } from 'components/common'
import { Slider, ProjectInfo, ProjectContent, MapInSingle, ProjectApproved, CatalogView, FormForGuest } from 'components/pages'

//* Helpers
import { config, arrayGenChild } from 'helpers'

//* Styles
import SingleInvestmentProjectStyle from 'styles/pages/SingleInvestmentProjectStyle'

const SingleInvestmentProjects = Memo(({ pageParams, global, selectedLang, winWidth, isLoggedIn, preloader, ...props }) => {
    //! Router
    const router = useRouter()

    //! Page Data
    const pageData = useMemo(
        () => props[pageParams.singleType]?.[router.query.slug]?.[selectedLang],
        [props?.[pageParams.singleType], router, selectedLang]
    )

    //! State
    const [secObj, setSecObj] = useState()

    //! Refs
    const TitleRef = useRef()
    const Ref1 = useRef()
    const Ref2 = useRef()
    const Ref3 = useRef()
    const Ref4 = useRef()
    const Ref5 = useRef()
    const Ref6 = useRef()
    const Ref7 = useRef()

    useEffect(() => {
        if (
            pageData &&
            isLoggedIn &&
            TitleRef?.current &&
            Ref1?.current?.[1] &&
            Ref2?.current &&
            Ref3?.current?.[0].current &&
            Ref4?.current &&
            Ref6?.current &&
            Ref7?.current &&
            !preloader
        ) {
            setSecObj([
                TitleRef.current,
                Ref1.current[0].current,
                Ref1.current[1].current,
                Ref2.current[0].current[0].current,
                Ref2.current[1].current,
                Ref2.current[2].current,
                Ref2.current[3].current,
                Ref2.current[4].current.childNodes,
                Ref2.current[5].current,
                Ref3.current[0].current.childNodes[0].childNodes,
                Ref3.current[0].current.childNodes[1].childNodes,
                Ref4.current[0].current.childNodes,
                Ref6.current[0].current.childNodes,
                arrayGenChild(Ref7.current[0].current.childNodes[0].childNodes),
                Ref7.current[2].current.childNodes,
                Ref7.current[3].current,
            ])
        } else if (
            (pageData && !isLoggedIn && TitleRef?.current && Ref1?.current?.[1] && Ref2?.current && Ref3?.current?.[0].current, Ref5?.current?.[0])
        ) {
            setSecObj([
                TitleRef.current,
                Ref1.current[0].current,
                Ref1.current[1].current,
                Ref2.current[0].current[0].current,
                Ref2.current[1].current,
                Ref2.current[2].current,
                Ref2.current[3].current,
                Ref2.current[4].current.childNodes,
                Ref3.current[0].current.childNodes[0].childNodes,
                Ref5.current[0].current.childNodes,
            ])
        }
    }, [TitleRef.current, Ref1.current, Ref2.current, Ref3.current, Ref4.current, Ref5.current, Ref6.current, Ref7.current, pageData, winWidth, preloader])

    return (
        <SingleInvestmentProjectStyle>
            <Page
                className='single-invest'
                meta={{
                    title: pageData?.project?.meta?.title,
                    description: pageData?.project?.meta?.description,
                    keywords: pageData?.project?.meta?.keywords,
                    image: pageData?.project.meta_image,
                }}
            >
                {pageData && (
                    <AnimationWrapp state={secObj}>
                        <Container className='top-container'>
                            <CustomLink
                                ref={TitleRef}
                                className='back-to'
                                url='/investment-projects'
                                content={
                                    <>
                                        <Icon className='icon-arrow-left ultraDarkGrey' />
                                        <Text tag={'p'} className={'pM MonRegular ultraDarkGrey'} text={'allProjects'} />
                                    </>
                                }
                            />
                            <div className='top'>
                                <Slider photos={pageData.project.photos} ref={Ref1} />
                                <ProjectInfo
                                    ref={Ref2}
                                    id={pageData.project.id}
                                    categorySlug={pageData.project.company.category_id.slug}
                                    categoryName={pageData.project.company.category_id.name}
                                    title={pageData.project.name}
                                    location={pageData.project.city.name}
                                    companyName={pageData.project.company.name}
                                    companyType={pageData.project.company.type}
                                    companyURL={config.routes.projectsCompanySingle.path.replace(':slug', pageData.project.company.slug)}
                                    priceDollar={`${pageData.project.required_investment.USD}`}
                                    priceDram={`${pageData.project.required_investment.AMD}`}
                                    priceDollarFull={`${pageData.project.total_cost.USD}`}
                                    priceDramFull={`${pageData.project.total_cost.AMD}`}
                                    profitPercent={`${pageData.project.margin_profitability}`}
                                    yearlySaleDollar={pageData.project.company.average_sales.USD}
                                    yearlySaleDram={pageData.project.company.average_sales.AMD}
                                    auctionsPercent={`${pageData.project.share_company}`}
                                    employersNumber={pageData.project.company.employees_number}
                                    paybackPeriod={pageData.project.payback_period}
                                    projectPriceTooltip={pageData.project?.project_price_tooltip}
                                    loggedIn={isLoggedIn}
                                    details={pageData.project?.details}
                                />

                                <ProjectContent
                                    ref={Ref3}
                                    aboutCompany={pageData.project.company.short_description}
                                    aboutInvestProject={pageData.project.short_description}
                                    bussinesPlan={pageData.project.bussines_plan}
                                />
                            </div>
                            {isLoggedIn ? (
                                <MapInSingle ref={Ref4} latitude={pageData.project.latitude} longitude={pageData.project.longitude} />
                            ) : (
                                <FormForGuest ref={Ref5} />
                            )}
                        </Container>
                        {isLoggedIn && (
                            <>
                                <ProjectApproved ref={Ref6} />
                                <CatalogView
                                    ref={Ref7}
                                    singleInvest={true}
                                    title={'youMightInterested'}
                                    catalogsArr={pageData.relatedProjects}
                                    url={config.routes.investmentProjects.path}
                                />
                            </>
                        )}
                    </AnimationWrapp>
                )}
            </Page>
        </SingleInvestmentProjectStyle>
    )
})

export default withDataContext(withAccountContext(withUIContext(SingleInvestmentProjects, ['winWidth']), ['isLoggedIn']), [
    'global',
    config.routes.singleInvestmentProject.singleType,
])
